import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  Loader2,
  XCircle,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogFooter,
} from '../ui/alert-dialog';
import OfficeModalHeader from './OfficeModalHeader';
import { subscribeOfficeNotify } from '../../lib/officeNotifyBus';
import { cn } from '../../lib/utils';

const URL_IN_TEXT = /(https?:\/\/[^\s]+)/i;

function FeedbackMessage({ message }) {
  const raw = String(message || '');
  if (!raw.trim()) return null;

  const match = raw.match(URL_IN_TEXT);
  if (match) {
    const url = match[1];
    const label = raw.replace(url, '').replace(/[:\s]+$/g, '').trim() || 'Link copiado';
    return (
      <span className="office-feedback-msg block text-[13px] leading-relaxed text-muted-foreground">
        {label ? <span className="block">{label}</span> : null}
        <code className="office-feedback-url">{url}</code>
      </span>
    );
  }

  return (
    <span className="office-feedback-msg block whitespace-pre-wrap text-[13px] leading-relaxed text-muted-foreground">
      {raw}
    </span>
  );
}

const FEEDBACK_META = {
  success: {
    icon: CheckCircle2,
    title: 'Tudo certo',
    tone: 'success',
    confirmLabel: 'Entendi',
  },
  error: {
    icon: XCircle,
    title: 'Não foi possível concluir',
    tone: 'error',
    confirmLabel: 'Entendi',
  },
  info: {
    icon: Info,
    title: 'Informação',
    tone: 'info',
    confirmLabel: 'OK',
  },
  warning: {
    icon: AlertTriangle,
    title: 'Atenção',
    tone: 'warning',
    confirmLabel: 'Entendi',
  },
  loading: {
    icon: Loader2,
    title: 'Aguarde',
    tone: 'loading',
    confirmLabel: null,
  },
};

export function OfficeFeedbackProvider({ children }) {
  const [current, setCurrent] = useState(null);
  const [open, setOpen] = useState(false);
  const queueRef = useRef([]);
  const loadingIdsRef = useRef(new Set());

  const present = useCallback((item) => {
    setCurrent(item);
    setOpen(true);
  }, []);

  const showNext = useCallback(() => {
    const next = queueRef.current.shift();
    if (next) {
      present(next);
    } else {
      setCurrent(null);
      setOpen(false);
    }
  }, [present]);

  const enqueue = useCallback(
    (item) => {
      if (item.type === 'loading') {
        loadingIdsRef.current.add(item.id);
        present(item);
        return;
      }

      if (item.id && loadingIdsRef.current.has(item.id)) {
        loadingIdsRef.current.delete(item.id);
        present(item);
        return;
      }

      // Substitui o modal atual se for o mesmo id (ex.: rate-limit).
      if (item.id && current?.id === item.id) {
        present(item);
        return;
      }

      // Não empilhar o mesmo erro várias vezes.
      const sameMsg = (a, b) =>
        String(a?.message || '') === String(b?.message || '') && a?.type === b?.type;
      if (current && sameMsg(current, item)) {
        return;
      }
      if (queueRef.current.some((q) => sameMsg(q, item) || (item.id && q.id === item.id))) {
        return;
      }

      if (!open && !current) {
        present(item);
        return;
      }

      if (current?.type === 'loading') {
        present(item);
        return;
      }

      queueRef.current.push(item);
    },
    [current, open, present],
  );

  const dismissCurrent = useCallback(() => {
    if (current?.type === 'loading') return;
    setOpen(false);
    setCurrent(null);
    setTimeout(showNext, 120);
  }, [current?.type, showNext]);

  useEffect(() => {
    return subscribeOfficeNotify((payload) => {
      if (payload?.type === 'dismiss') {
        loadingIdsRef.current.delete(payload.id);
        queueRef.current = queueRef.current.filter((x) => x.id !== payload.id);
        setCurrent((c) => {
          if (c?.id === payload.id) {
            setTimeout(showNext, 0);
            return null;
          }
          return c;
        });
        return;
      }
      if (!payload?.type) return;
      enqueue(payload);
    });
  }, [enqueue, showNext]);

  const meta = current ? FEEDBACK_META[current.type] || FEEDBACK_META.info : null;
  const Icon = meta?.icon;
  const isLoading = current?.type === 'loading';

  return (
    <>
      {children}
      <AlertDialog
        open={open && !!current}
        onOpenChange={(next) => {
          if (!next && !isLoading) dismissCurrent();
        }}
      >
        <AlertDialogContent
          className={cn(
            'office-feedback-dialog border-border bg-card text-foreground',
            meta?.tone && `office-feedback-dialog--${meta.tone}`,
          )}
          data-testid="office-feedback-dialog"
        >
          {current && meta ? (
            <>
              <OfficeModalHeader
                asAlert
                layout="row"
                variant="circle"
                icon={Icon}
                title={current.title || meta.title}
                description={
                  current.message ? <FeedbackMessage message={current.message} /> : undefined
                }
                className={cn(isLoading && '[&_.office-modal-icon_svg]:animate-spin')}
              />
              {!isLoading ? (
                <AlertDialogFooter className="office-dialog-footer sm:justify-end">
                  <AlertDialogAction
                    className={cn(
                      'min-w-[7rem] font-semibold',
                      meta.tone === 'error' &&
                        'bg-destructive text-destructive-foreground hover:bg-destructive/90',
                      meta.tone === 'success' &&
                        'bg-emerald-600 text-white hover:bg-emerald-600/90 dark:bg-emerald-600',
                    )}
                    data-testid="office-feedback-ok"
                  >
                    {meta.confirmLabel}
                  </AlertDialogAction>
                </AlertDialogFooter>
              ) : (
                <div className="pb-1 pt-2">
                  <div className="office-feedback-loading-bar" aria-hidden />
                </div>
              )}
            </>
          ) : null}
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default OfficeFeedbackProvider;
